import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import type { QueryClient } from '@tanstack/react-query';
import type { useCreateObject, useUpdateObject } from '@/features/objects/hooks';
import { getEventEtag } from '../lib/getEventEtag';
import type { EventObject } from '../eventTypes';

/**
 * Recurrence mutation engine extracted from EventListModule (#3277).
 *
 * `handleRecurringAction` is the 6-way branch matrix
 *   mode ∈ {delete, edit} × action ∈ {this, following, all}
 * that decides whether to deleteObject / createEvent / updateEvent for a
 * recurring event, and with exactly what payload (RRULE UNTIL truncation,
 * cancelled-occurrence exceptions, virtual-vs-exception edits, split-the-series
 * creates), then invalidates the events query and runs the cleanup trio.
 *
 * Plain (non-memoized) async function invoked only on user action; the live
 * deps are threaded in via typed options at call time. The RRULE/UNTIL math
 * (originalStart − 1 day → compacted YYYYMMDDTHHMMSSZ, stripping any pre-existing
 * UNTIL/COUNT) is pinned by `useRecurringEventActions.char.test.ts`.
 */
export interface RecurringDialogState {
  mode: 'edit' | 'delete';
  event: any; // the virtual or exception event
  pendingData?: Record<string, any>; // for edit: the eventData to save
}

export interface UseRecurringEventActionsOptions {
  recurringDialog: RecurringDialogState | null;
  allEvents: any[];
  moduleId: string;
  token: string | null;
  createEvent: ReturnType<typeof useCreateObject<any, EventObject>>;
  updateEvent: ReturnType<typeof useUpdateObject<EventObject>>;
  queryClient: QueryClient;
  setRecurringDialog: (value: RecurringDialogState | null) => void;
  handleBack: () => void;
}

export function useRecurringEventActions(opts: UseRecurringEventActionsOptions) {
  const {
    recurringDialog,
    allEvents,
    moduleId,
    token,
    createEvent,
    updateEvent,
    queryClient,
    setRecurringDialog,
    handleBack,
  } = opts;

  const handleRecurringAction = async (action: 'this' | 'following' | 'all') => {
    if (!recurringDialog) return;
    const { mode, event: evt, pendingData } = recurringDialog;
    const masterId = evt._masterEventId || evt.recurringEventId || evt.id;
    const originalStart = evt._originalStartAt || evt.originalStartAt;

    try {
      if (mode === 'delete') {
        if (action === 'all') {
          await api.objects.deleteObject(token ?? null, 'event', masterId);
        } else if (action === 'this') {
          // Cancelled exception, not archived=true: the API excludes archived objects from queries.
          const masterEvent = allEvents.find((e: any) => e.id === masterId);
          if (masterEvent) {
            createEvent.mutate({
              title: masterEvent.title || 'Cancelled occurrence',
              startAt: evt.startAt,
              endAt: evt.endAt,
              recurringEventId: masterId,
              originalStartAt: originalStart,
              cancelledOccurrence: true,
              moduleLinks: [{ moduleId }],
            });
          }
        } else if (action === 'following') {
          // Update master RRULE with UNTIL before this occurrence
          const masterEvent = allEvents.find((e: any) => e.id === masterId);
          if (masterEvent) {
            const { etag } = await getEventEtag(masterEvent);
            const untilDate = new Date(new Date(originalStart).getTime() - 86400000);
            const untilStr = untilDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '').replace('Z', '') + 'Z';
            let updatedRrule = masterEvent.rrule || '';
            updatedRrule = updatedRrule.replace(/;?(UNTIL|COUNT)=[^;]*/g, '');
            updatedRrule += `;UNTIL=${untilStr}`;
            updateEvent.mutate({ id: masterId, etag, patch: { rrule: updatedRrule } });
          }
        }
      } else if (mode === 'edit' && pendingData) {
        if (action === 'all') {
          // Update the master event directly
          const { etag } = await getEventEtag({ id: masterId } as any);
          updateEvent.mutate({ id: masterId, etag, patch: pendingData });
        } else if (action === 'this') {
          if (evt._isVirtual) {
            // Create a new exception event
            createEvent.mutate({
              ...pendingData,
              recurringEventId: masterId,
              originalStartAt: originalStart,
              rrule: null,
              moduleLinks: [{ moduleId }],
            });
          } else {
            // Update existing exception
            const { etag } = await getEventEtag(evt);
            updateEvent.mutate({ id: evt.id, etag, patch: pendingData });
          }
        } else if (action === 'following') {
          // Truncate master at this point, create new master with updated data
          const masterEvent = allEvents.find((e: any) => e.id === masterId);
          if (masterEvent) {
            const { etag } = await getEventEtag(masterEvent);
            const untilDate = new Date(new Date(originalStart).getTime() - 86400000);
            const untilStr = untilDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '').replace('Z', '') + 'Z';
            let truncatedRrule = masterEvent.rrule || '';
            truncatedRrule = truncatedRrule.replace(/;?(UNTIL|COUNT)=[^;]*/g, '');
            truncatedRrule += `;UNTIL=${untilStr}`;
            updateEvent.mutate({ id: masterId, etag, patch: { rrule: truncatedRrule } });
            // Create new master starting from this occurrence
            createEvent.mutate({
              ...pendingData,
              rrule: masterEvent.rrule, // keep original rrule (without the UNTIL we just set)
              recurringEventId: undefined,
              originalStartAt: undefined,
              moduleLinks: [{ moduleId }],
            });
          }
        }
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
    } catch (err) {
      console.error('[EventListModule] Recurring action failed:', err);
    }
    setRecurringDialog(null);
    handleBack();
  };

  return { handleRecurringAction };
}
